'use client';

/**
 * ContentBlock - Unified content rendering
 * 
 * Renders title, subtitle, description, and badge with configurable styling.
 */

import React from 'react';
import { ContentBlockConfig, AnimationConfig } from '../types';
import { SmoothReveal } from '@/components/shared/SmoothReveal';
import { getBrandColor } from '@/config/brand-colors';

interface ContentBlockProps {
  config?: ContentBlockConfig;
  animation?: AnimationConfig;
  className?: string;
}

const titleSizeClasses: Record<string, string> = {
  sm: 'text-lg md:text-xl',
  md: 'text-xl md:text-2xl',
  lg: 'text-2xl md:text-3xl',
  xl: 'text-3xl md:text-4xl',
  '2xl': 'text-4xl md:text-5xl',
  '3xl': 'text-5xl md:text-6xl',
  '4xl': 'text-6xl md:text-7xl lg:text-8xl',
};

const descriptionSizeClasses: Record<string, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg md:text-xl',
};

const alignmentClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center mx-auto',
  right: 'text-right ml-auto',
};

export function ContentBlock({ config, animation, className = '' }: ContentBlockProps) {
  if (!config) return null;

  const {
    badge,
    badgeColor,
    title,
    subtitle,
    description,
    alignment = 'left',
    titleSize = 'xl',
    descriptionSize = 'md',
    textColor,
    maxWidth,
  } = config;

  const alignClass = alignmentClasses[alignment] || alignmentClasses.left;
  const titleClass = titleSizeClasses[titleSize] || titleSizeClasses.xl;
  const descClass = descriptionSizeClasses[descriptionSize] || descriptionSizeClasses.md;

  const containerStyle: React.CSSProperties = {
    maxWidth: maxWidth || undefined,
    color: textColor || undefined,
  };

  const delay = animation?.delay || 0;
  const stagger = animation?.stagger || 0.08;

  return (
    <div className={`${alignClass} ${className}`} style={containerStyle}>
      {/* Badge */}
      {badge && (
        <SmoothReveal delay={delay}>
          <span 
            className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{
              backgroundColor: badgeColor ? `${badgeColor}20` : `${getBrandColor('accentOrange')}20`,
              color: badgeColor || getBrandColor('accentOrange'),
            }}
          >
            {badge}
          </span>
        </SmoothReveal>
      )}

      {/* Subtitle (above title) */}
      {subtitle && (
        <SmoothReveal delay={delay + stagger}>
          <p 
            className="text-sm md:text-base font-medium mb-2 opacity-80"
          >
            {subtitle}
          </p>
        </SmoothReveal>
      )}

      {/* Title */}
      {title && (
        <SmoothReveal delay={delay + stagger * 2}>
          <h2 
            className={`${titleClass} font-bold mb-4`}
            style={{ color: textColor || getBrandColor('deepNavy') }}
          >
            {title}
          </h2>
        </SmoothReveal>
      )}

      {/* Description */}
      {description && (
        <SmoothReveal delay={delay + stagger * 3}>
          <div 
            className={`${descClass} leading-relaxed prose prose-invert max-w-none [&_p]:my-2 [&_strong]:font-bold [&_em]:italic`}
            style={{ color: textColor ? undefined : '#4a4a4a' }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </SmoothReveal>
      )}
    </div>
  );
}

export default ContentBlock;
